import Link from 'next/link';
import { BsPencil } from 'react-icons/bs';
import { IoQrCodeSharp, IoSparkles, IoStatsChart } from 'react-icons/io5';

import {
    Anchor,
    Badge,
    Box,
    Button,
    Divider,
    Group,
    HoverCard,
    Loader,
    RingProgress,
    Stack,
    Text,
} from '@mantine/core';

import { CompanyProfile, PermissionCompany } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { MarketingHelpers } from 'helpers/MarketingHelpers';

import { AIMarkdown } from 'components/ai-markdown/AIMarkdown';
import { ResponsiveActionButton } from 'components/buttons/ResponsiveActionButton';
import { ProgressChecklist } from 'components/company-profile/components/ProgressChecklist';
import { RequestAccess } from 'components/company-profile/components/RequestAccess';
import { useProfilePermission } from 'components/company-profile/hooks/use-profile-permission';
import { useProfileProgress } from 'components/company-profile/hooks/use-profile-progress';
import { useCurrentUser } from 'hooks/use-current-user';
import { useLocalEvent } from 'hooks/use-local-event';
import { useQrScansByCompany } from 'hooks/use-qr-scans-by-company';
import { useQrScansSummary } from 'hooks/use-qr-scans-summary';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { FeedbackService } from 'services/FeedbackService';

const ProfileActions = ({ company }: { company: CompanyProfile }) => {
    const { localEvent: event } = useLocalEvent();

    const canEdit = useProfilePermission(PermissionCompany.EDIT);
    const user = useCurrentUser();
    const { workspace: inWorkspace, showAnalytics } = useWorkspaceConfig();

    const openFeedback = () => {
        FeedbackService.open({
            formTitle: `Suggest Change for ${company.name}`,
            messagePlaceholder: 'What changes would you like to suggest?',
        });
    };

    if (!canEdit) {
        return (
            <>
                {company.internal && (
                    <ResponsiveActionButton
                        icon={<BsPencil />}
                        breakpoint="sm"
                        label="Suggest a Change"
                        actionIconProps={{
                            onClick: openFeedback,
                            variant: 'default',
                            size: 36,
                        }}
                        buttonProps={{
                            onClick: openFeedback,
                            variant: 'default',
                            size: 'sm',
                        }}
                    />
                )}

                {showAnalytics && user?.internal && <AnalyticsButton company={company} />}

                <RequestAccess company={company} />
            </>
        );
    }

    return (
        <>
            {!inWorkspace && <QRButton company={company} event={event} />}

            {showAnalytics && <AnalyticsButton company={company} />}

            <EditProfileButton company={company} />
        </>
    );
};

const AnalyticsButton = ({ company }: { company: CompanyProfile }) => {
    const analyticsUrl = CompanyHelpers.urls.edit(company.slug, 'analytics');

    return (
        <ResponsiveActionButton
            icon={<IoStatsChart />}
            breakpoint="sm"
            label="Analytics"
            actionIconProps={{ variant: 'default', size: 36, component: 'a', href: analyticsUrl }}
            buttonProps={{ variant: 'default', size: 'sm', component: 'a', href: analyticsUrl }}
        />
    );
};

const EditProfileButton = ({ company }: { company: CompanyProfile }) => {
    const { percentage, color } = useProfileProgress(company);

    const editUrl = CompanyHelpers.urls.edit(company.slug);
    const isIncomplete = percentage !== null && percentage < 100;

    const button = (
        <ResponsiveActionButton
            icon={
                isIncomplete ? (
                    <RingProgress
                        size={22}
                        thickness={3}
                        roundCaps
                        rootColor="white"
                        sections={[{ value: percentage, color: color ?? 'dark' }]}
                    />
                ) : (
                    <BsPencil size={12} />
                )
            }
            breakpoint="sm"
            label="Edit profile"
            actionIconProps={{ variant: 'filled', size: 36, component: 'a', href: editUrl }}
            buttonProps={{ variant: 'filled', size: 'sm', component: 'a', href: editUrl }}
        />
    );

    if (!isIncomplete) return button;

    return (
        <HoverCard width={320} shadow="md" openDelay={200} withArrow>
            <HoverCard.Target>
                <div>{button}</div>
            </HoverCard.Target>
            <HoverCard.Dropdown>
                <Stack gap="md">
                    <Group gap="sm" wrap="nowrap">
                        <RingProgress
                            size={56}
                            thickness={5}
                            roundCaps
                            sections={[{ value: percentage, color: color ?? 'dark' }]}
                            label={
                                <Text fz="xs" fw={700} ta="center">
                                    {percentage}%
                                </Text>
                            }
                        />
                        <Box>
                            <Text fw={700}>Your profile is {percentage}% complete</Text>
                            <Text fz="sm" c="dimmed">
                                A complete profile shows visitors what you offer and how to reach you.
                            </Text>
                        </Box>
                    </Group>

                    <ProgressChecklist company={company} remainingOnly />

                    <Button component={Link} href={editUrl} size="sm" color="dark" fullWidth>
                        Complete profile
                    </Button>
                </Stack>
            </HoverCard.Dropdown>
        </HoverCard>
    );
};

const QRButton = ({ company, event }: { company: CompanyProfile; event?: { name: string } | null }) => {
    const { leads } = useQrScansByCompany({ companyId: company.id });
    const { summary, isSummarizing } = useQrScansSummary({ companyId: company.id });

    const leadCount = leads.length;

    return (
        <HoverCard width={360} shadow="md" openDelay={200} withArrow>
            <HoverCard.Target>
                <div>
                    <ResponsiveActionButton
                        icon={<IoQrCodeSharp />}
                        breakpoint="sm"
                        label={
                            <>
                                Showtime Leads
                                {!!leadCount && (
                                    <Badge color="red" fz={8} py={0} size="xs" ml={8}>
                                        {leadCount}
                                    </Badge>
                                )}
                            </>
                        }
                        actionIconProps={{
                            component: 'a',
                            href: CompanyHelpers.urls.showtimeLeads(company.slug),
                            variant: 'default',
                            size: 36,
                        }}
                        buttonProps={{
                            component: 'a',
                            href: CompanyHelpers.urls.showtimeLeads(company.slug),
                            variant: 'default',
                            size: 'sm',
                        }}
                    />
                </div>
            </HoverCard.Target>
            <HoverCard.Dropdown>
                <Stack gap="sm">
                    {!leadCount ? (
                        <Text>No leads yet. Share your QR codes to start collecting leads!</Text>
                    ) : (
                        <>
                            <Box>
                                <Group gap={4} mb={4}>
                                    <IoSparkles size={14} style={{ color: 'var(--mantine-color-brand-4)' }} />
                                    <Text fw={600} c="brand.4">
                                        QR Scans - AI Insights
                                    </Text>
                                </Group>
                                {isSummarizing && <Loader size="xs" />}
                                {summary && !isSummarizing && (
                                    <Text>
                                        <AIMarkdown>{summary.summary}</AIMarkdown>
                                    </Text>
                                )}
                            </Box>
                            <Divider />
                        </>
                    )}

                    <Group gap="xs">
                        <Button
                            component="a"
                            size="sm"
                            variant="outline"
                            href={CompanyHelpers.urls.showtimeLeads(company.slug)}
                            flex={1}
                        >
                            View Full Report
                        </Button>
                        <Button
                            component="a"
                            size="sm"
                            variant="filled"
                            href={CompanyHelpers.urls.qr(company.slug)}
                            leftSection={<IoQrCodeSharp size={16} />}
                            flex={1}
                        >
                            {event ? `${event.name} QR Codes` : 'My QR Codes'}
                        </Button>
                    </Group>

                    <Anchor
                        component={Link}
                        href={MarketingHelpers.urls.resourcepro.qrCodes()}
                        fw={600}
                        fz="sm"
                        ta="center"
                    >
                        Learn more about QR codes
                    </Anchor>
                </Stack>
            </HoverCard.Dropdown>
        </HoverCard>
    );
};

export { ProfileActions };
